import { Dialog } from '@angular/cdk/dialog';
import { SecurityEventProtocolApiService } from '@core/api/security-event-protocol-api.service';
import { inject, Injectable } from '@angular/core';
import { SecurityEventsGridService } from '../security-events-grid.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { ToastService } from 'ngx-mt-ui-lib/services/toast';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { SecurityEventsActions } from './security-events.actions';
import {
  catchError,
  distinctUntilChanged,
  EMPTY,
  filter,
  from,
  map,
  of,
  switchMap,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs';
import {
  InitEventSecurityApiService,
  IInitEventSecurityLinkChild,
  IInitEventSecurityInfo,
  IInitEventSecurityGridCount,
  REACTIVE_API_TOPICS,
  ReactiveApiBrokerService,
  EInitEventStateCode,
  EInitEventTypeCode,
} from 'ngx-mt-ui-lib/api-client';
import { ISelectItem } from 'ngx-mt-ui-lib/interfaces';
import { openLazyDialog } from '@app/shared/functions/open-lazy-dialog.function';
import {
  selectSecurityEventsHasChanges,
  selectSecurityEventsId,
  selectSecurityEventsInfoRevision,
  selectSecurityEventsItem,
} from './security-events.selectors';
import { openConfirmDialog } from 'ngx-mt-ui-lib/components/dialog';
import { ReasonDialogComponent } from '@app/shared/components/reason-dialog/reason-dialog.component';
import {
  IReasonDialogData,
  IReasonDialogResult,
} from '@app/core/interfaces/reason-dialog.interface';
import { parseError } from 'ngx-mt-ui-lib/functions';
import { EInitEventCardUrl } from '@app/app.enums';

@Injectable()
export class SecurityEventsEffects {
  private readonly securityEventProtocolApi = inject(
    SecurityEventProtocolApiService,
  );
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly dialog = inject(Dialog);
  private readonly securityEventsGridService = inject(
    SecurityEventsGridService,
  );
  private readonly securityEventsApiService = inject(
    InitEventSecurityApiService,
  );
  private readonly reactiveApiBroker = inject(ReactiveApiBrokerService);

  readonly selectItem$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SecurityEventsActions.selectItem),
        tap(({ id }) => {
          if (typeof id === 'number') {
            this.store.dispatch(SecurityEventsActions.requestItemInfo({ id }));
          }
        }),
      ),
    { dispatch: false },
  );

  readonly watchSecurityEventInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SecurityEventsActions.watchSecurityEventInfo),
      switchMap(() =>
        this.store.select(selectSecurityEventsId).pipe(
          distinctUntilChanged(),
          switchMap((securityEventId) => {
            if (typeof securityEventId !== 'number' || !securityEventId) {
              return EMPTY;
            }

            return this.reactiveApiBroker
              .watchParsed<IInitEventSecurityInfo>(
                REACTIVE_API_TOPICS.securityEventInfo(securityEventId),
              )
              .pipe(
                filter(
                  (item): item is IInitEventSecurityInfo =>
                    !!item &&
                    typeof item === 'object' &&
                    typeof item.id === 'number',
                ),
                map((item) =>
                  SecurityEventsActions.securityEventInfoReceived({ item }),
                ),
              );
          }),
          takeUntil(
            this.actions$.pipe(
              ofType(SecurityEventsActions.stopWatchSecurityEventInfo),
            ),
          ),
        ),
      ),
    ),
  );

  readonly requestItemInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SecurityEventsActions.requestItemInfo),
      withLatestFrom(this.store.select(selectSecurityEventsInfoRevision)),
      switchMap(([{ id }, observedRevision]) => {
        const api = this.securityEventsApiService.getInfo(id);
        return api.pipe(
          map((item) =>
            SecurityEventsActions.requestItemInfoSuccess({
              item,
              observedRevision,
            }),
          ),
          catchError((error) =>
            of(SecurityEventsActions.requestItemInfoError({ error })),
          ),
        );
      }),
    ),
  );

  readonly requestItemInfoSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SecurityEventsActions.requestItemInfoSuccess),
        tap(() => {
          this.store.dispatch(SecurityEventsActions.requestChildEventList());
          this.store.dispatch(SecurityEventsActions.requestTags());
        }),
      ),
    { dispatch: false },
  );

  readonly getStateListWithCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SecurityEventsActions.getStateListWithCount),
      switchMap(({ body }) =>
        this.securityEventsApiService.getCountForStates(body).pipe(
          map((res) => {
            return SecurityEventsActions.getStateListWithCountSuccess({
              list: this.getStateList(res),
            });
          }),
          catchError((error) =>
            of(SecurityEventsActions.getStateListWithCountError({ error })),
          ),
        ),
      ),
    ),
  );

  readonly openCreateDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SecurityEventsActions.openCreateDialog),
      switchMap(({ parentId, prefill, sourceMessage }) =>
        openLazyDialog<IInitEventSecurityInfo>(
          this.dialog,
          () =>
            import('../security-events-add-dialog/security-events-add-dialog.component'),
          'SecurityEventsAddDialogComponent',
          {
            autoFocus: false,
            panelClass: 'aligned-right-dialog',
            data: {
              prefill,
              parentId,
              sourceMessage,
            },
          },
        ).pipe(
          switchMap((body) => {
            if (!body) {
              return EMPTY;
            }

            return of(
              SecurityEventsActions.requestCreateItemSuccess({ item: body }),
            );
          }),
        ),
      ),
    ),
  );

  readonly requestCreateItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SecurityEventsActions.requestCreateItem),
      switchMap(({ body }) => {
        const req$ = this.securityEventProtocolApi.create(body);
        return req$.pipe(
          map((item) =>
            SecurityEventsActions.requestCreateItemSuccess({
              item,
            }),
          ),
          catchError((error) =>
            of(SecurityEventsActions.requestCreateItemError({ error })),
          ),
        );
      }),
    ),
  );

  readonly requestCreateItemSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SecurityEventsActions.requestCreateItemSuccess),
        tap(({ item }) => {
          this.securityEventsGridService.refreshGrid();
          this.router.navigate([`${EInitEventCardUrl.SB}/${item.id}`]);
        }),
      ),
    { dispatch: false },
  );

  readonly requestUpdateItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SecurityEventsActions.requestUpdateItem),
      switchMap(({ id, body }) => {
        const api = this.securityEventsApiService.update(id, body);
        return api.pipe(
          map((item) =>
            SecurityEventsActions.requestUpdateItemSuccess({
              item,
            }),
          ),
          catchError((error) =>
            of(SecurityEventsActions.requestUpdateItemError({ error })),
          ),
        );
      }),
    ),
  );

  readonly requestChildEventList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SecurityEventsActions.requestChildEventList),
      withLatestFrom(this.store.select(selectSecurityEventsId)),
      switchMap(([_, securityEventId]) =>
        this.securityEventsApiService
          .getLinkChildrenEventList(securityEventId)
          .pipe(
            map((childEventList: IInitEventSecurityLinkChild[]) =>
              SecurityEventsActions.requestChildEventListSuccess({
                childEventList,
              }),
            ),
            catchError((error) =>
              of(SecurityEventsActions.requestChildEventListError({ error })),
            ),
          ),
      ),
    ),
  );

  readonly openAddParentSecurityEventModal$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SecurityEventsActions.openAddParentSecurityEventModal),
        switchMap(({ securityEventId }) =>
          openLazyDialog(
            this.dialog,
            () =>
              import('../../shared/add-parent-security-event-modal/add-parent-security-event-modal.component'),
            'AddParentSecurityEventModalComponent',
            {
              autoFocus: false,
              data: {
                eventId: securityEventId,
                eventTypeCode: EInitEventTypeCode.SB,
              },
              panelClass: 'aligned-right-dialog',
            },
          ),
        ),
      ),
    { dispatch: false },
  );

  readonly attemptRequestAddParentSecurityEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SecurityEventsActions.attemptRequestAddParentSecurityEvent),
      withLatestFrom(this.store.select(selectSecurityEventsHasChanges)),
      switchMap(([params, hasChanges]) => {
        if (!hasChanges) {
          return of(
            SecurityEventsActions.requestAddParentSecurityEvent(params),
          );
        }

        const ref = openConfirmDialog(this.dialog);

        return from(ref.closed).pipe(
          switchMap((res) => {
            if (!res) {
              return EMPTY;
            }

            return of(
              SecurityEventsActions.requestAddParentSecurityEvent(params),
            );
          }),
        );
      }),
    ),
  );

  readonly requestAddParentSecurityEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SecurityEventsActions.requestAddParentSecurityEvent),
      switchMap(({ securityEventId, securityEventLinkId }) =>
        this.securityEventsApiService
          .addParentSecurityEvent(securityEventId, securityEventLinkId)
          .pipe(
            map(() =>
              SecurityEventsActions.requestAddParentSecurityEventSuccess(),
            ),
            catchError((error) =>
              of(
                SecurityEventsActions.requestAddParentSecurityEventError({
                  error,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  readonly openAddChildSecurityEventModal$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SecurityEventsActions.openAddChildSecurityEventModal),
        switchMap(({ securityEventId }) =>
          openLazyDialog(
            this.dialog,
            () =>
              import('../../shared/add-child-security-event-modal/add-child-security-event-modal.component'),
            'AddChildSecurityEventModalComponent',
            {
              autoFocus: false,
              data: {
                eventId: securityEventId,
                eventTypeCode: EInitEventTypeCode.SB,
              },
              panelClass: 'aligned-right-dialog',
            },
          ),
        ),
      ),
    { dispatch: false },
  );

  readonly attemptRequestAddChildSecurityEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SecurityEventsActions.attemptRequestAddChildSecurityEvent),
      withLatestFrom(this.store.select(selectSecurityEventsHasChanges)),
      switchMap(([params, hasChanges]) => {
        if (!hasChanges) {
          return of(SecurityEventsActions.requestAddChildSecurityEvent(params));
        }

        const ref = openConfirmDialog(this.dialog);

        return from(ref.closed).pipe(
          switchMap((res) => {
            if (!res) {
              return EMPTY;
            }

            return of(
              SecurityEventsActions.requestAddChildSecurityEvent(params),
            );
          }),
        );
      }),
    ),
  );

  readonly requestAddChildSecurityEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SecurityEventsActions.requestAddChildSecurityEvent),
      switchMap(({ securityEventId, securityEventLinkId }) =>
        this.securityEventsApiService
          .addChildSecurityEvent(securityEventId, securityEventLinkId)
          .pipe(
            map(() =>
              SecurityEventsActions.requestAddChildSecurityEventSuccess(),
            ),
            catchError((error) =>
              of(
                SecurityEventsActions.requestAddChildSecurityEventError({
                  error,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  readonly requestAddChildSecurityEventSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SecurityEventsActions.requestAddChildSecurityEventSuccess),
      withLatestFrom(this.store.select(selectSecurityEventsId)),
      switchMap(() => of(SecurityEventsActions.requestChildEventList())),
    ),
  );

  readonly openAddChildDisruptionModal$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SecurityEventsActions.openAddChildDisruptionModal),
        switchMap(({ securityEventId }) =>
          openLazyDialog(
            this.dialog,
            () =>
              import('../../shared/add-child-disruption-modal/add-child-disruption-modal.component'),
            'AddChildDisruptionModalComponent',
            {
              autoFocus: false,
              data: {
                eventId: securityEventId,
                eventTypeCode: EInitEventTypeCode.SB,
              },
              panelClass: 'aligned-right-dialog',
            },
          ),
        ),
      ),
    { dispatch: false },
  );

  readonly attemptRequestAddChildDisruption$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SecurityEventsActions.attemptRequestAddChildDisruption),
      withLatestFrom(this.store.select(selectSecurityEventsHasChanges)),
      switchMap(([params, hasChanges]) => {
        if (!hasChanges) {
          return of(SecurityEventsActions.requestAddChildDisruption(params));
        }

        const ref = openConfirmDialog(this.dialog);

        return from(ref.closed).pipe(
          switchMap((res) => {
            if (!res) {
              return EMPTY;
            }

            return of(SecurityEventsActions.requestAddChildDisruption(params));
          }),
        );
      }),
    ),
  );

  readonly requestAddChildDisruption$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SecurityEventsActions.requestAddChildDisruption),
      switchMap(({ securityEventId, disruptionLinkId }) =>
        this.securityEventsApiService
          .addChildDisruption(securityEventId, disruptionLinkId)
          .pipe(
            map(() => SecurityEventsActions.requestAddChildDisruptionSuccess()),
            catchError((error) =>
              of(
                SecurityEventsActions.requestAddChildDisruptionError({
                  error,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  readonly requestAddChildDisruptionSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SecurityEventsActions.requestAddChildDisruptionSuccess),
      withLatestFrom(this.store.select(selectSecurityEventsId)),
      switchMap(() => of(SecurityEventsActions.requestChildEventList())),
    ),
  );

  readonly requestDeleteChildEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SecurityEventsActions.requestDeleteChildEvent),
      switchMap(({ securityEventId, disruptionLinkId, eventTypeCode }) => {
        const api =
          eventTypeCode === EInitEventTypeCode.NNR
            ? this.securityEventsApiService.deleteChildDisruption(
                securityEventId,
                disruptionLinkId,
              )
            : this.securityEventsApiService.deleteChildSecurityEvent(
                securityEventId,
                disruptionLinkId,
              );
        return api.pipe(
          map(() => SecurityEventsActions.requestDeleteChildEventSuccess()),
          catchError((error) =>
            of(
              SecurityEventsActions.requestDeleteChildEventError({
                error,
                eventLinkId: disruptionLinkId,
              }),
            ),
          ),
        );
      }),
    ),
  );

  readonly requestDeleteChildEventSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SecurityEventsActions.requestDeleteChildEventSuccess),
      withLatestFrom(this.store.select(selectSecurityEventsId)),
      switchMap(() => of(SecurityEventsActions.requestChildEventList())),
    ),
  );

  //#region Смена статуса
  readonly attemptSetToWorking$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SecurityEventsActions.attemptSetToWorking),
      withLatestFrom(this.store.select(selectSecurityEventsHasChanges)),
      switchMap(([action, hasChanges]) => {
        if (!hasChanges) {
          return of(
            SecurityEventsActions.requestSetToWorking({ id: action.id }),
          );
        }
        const ref = openConfirmDialog(this.dialog);
        return ref.closed.pipe(
          switchMap((res) => {
            if (!res) {
              return EMPTY;
            }
            return of(
              SecurityEventsActions.requestSetToWorking({ id: action.id }),
            );
          }),
        );
      }),
    ),
  );

  readonly requestSetToWorking$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SecurityEventsActions.requestSetToWorking),
      switchMap((action) =>
        this.securityEventsApiService.setToWorking(action.id).pipe(
          map(() =>
            SecurityEventsActions.requestSetToWorkingSuccess({ id: action.id }),
          ),
          catchError((error) =>
            of(SecurityEventsActions.requestSetToWorkingError({ error })),
          ),
        ),
      ),
    ),
  );

  readonly attemptSetToCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SecurityEventsActions.attemptSetToCompleted),
      withLatestFrom(this.store.select(selectSecurityEventsHasChanges)),
      switchMap(([action, hasChanges]) => {
        if (!hasChanges) {
          return of(
            SecurityEventsActions.requestSetToCompleted({ id: action.id }),
          );
        }
        const ref = openConfirmDialog(this.dialog);
        return ref.closed.pipe(
          switchMap((res) => {
            if (!res) {
              return EMPTY;
            }
            return of(
              SecurityEventsActions.requestSetToCompleted({ id: action.id }),
            );
          }),
        );
      }),
    ),
  );

  readonly requestSetToCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SecurityEventsActions.requestSetToCompleted),
      switchMap((action) =>
        this.securityEventsApiService.setToCompleted(action.id).pipe(
          map(() =>
            SecurityEventsActions.requestSetToCompletedSuccess({
              id: action.id,
            }),
          ),
          catchError((error) =>
            of(SecurityEventsActions.requestSetToCompletedError({ error })),
          ),
        ),
      ),
    ),
  );

  readonly requestSetToClose$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SecurityEventsActions.requestSetToClose),
      switchMap((action) =>
        this.securityEventsApiService.setToClose(action.id).pipe(
          map(() =>
            SecurityEventsActions.requestSetToCloseSuccess({
              id: action.id,
            }),
          ),
          catchError((error) =>
            of(SecurityEventsActions.requestSetToCloseError({ error })),
          ),
        ),
      ),
    ),
  );

  readonly attemptSetToCancel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SecurityEventsActions.attemptSetToCancel),
      switchMap(({ id }) => {
        if (!id) {
          return EMPTY;
        }

        const ref = this.dialog.open<
          IReasonDialogResult,
          IReasonDialogData,
          ReasonDialogComponent
        >(ReasonDialogComponent, {
          autoFocus: false,
          data: {
            title: this.translateService.instant(
              'EVENT.SECURITY.CARD.FUNCTIONS.SET_TO_CANCEL.TITLE',
            ),
          },
        });

        return ref.closed.pipe(
          switchMap((res) => {
            if (!res) {
              return EMPTY;
            }

            return of(
              SecurityEventsActions.requestSetToCancel({
                id,
                reason: res.reason,
              }),
            );
          }),
        );
      }),
    ),
  );

  readonly requestSetToCancel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SecurityEventsActions.requestSetToCancel),
      switchMap(({ id, reason }) =>
        this.securityEventsApiService.setToCancel(id, reason).pipe(
          map(() => SecurityEventsActions.requestSetToCancelSuccess({ id })),
          catchError((error) =>
            of(SecurityEventsActions.requestSetToCancelError({ error })),
          ),
        ),
      ),
    ),
  );

  readonly requestSetToArchive$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SecurityEventsActions.requestSetToArchive),
      switchMap((action) =>
        this.securityEventsApiService.setToArchive(action.id).pipe(
          map(() =>
            SecurityEventsActions.requestSetToArchiveSuccess({
              id: action.id,
            }),
          ),
          catchError((error) =>
            of(SecurityEventsActions.requestSetToArchiveError({ error })),
          ),
        ),
      ),
    ),
  );

  readonly updateInfoAfterChangeState$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          SecurityEventsActions.requestSetToWorkingSuccess,
          SecurityEventsActions.requestSetToCompletedSuccess,
          SecurityEventsActions.requestSetToCloseSuccess,
          SecurityEventsActions.requestSetToCancelSuccess,
          SecurityEventsActions.requestSetToArchiveSuccess,
        ),
        tap(() => {
          this.securityEventsGridService.refreshGrid();
        }),
      ),
    { dispatch: false },
  );
  //#endregion

  readonly requestTags$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SecurityEventsActions.requestTags),
      withLatestFrom(this.store.select(selectSecurityEventsId)),
      switchMap(([_, securityEventId]) =>
        this.securityEventsApiService.getTags(securityEventId).pipe(
          map((tags) =>
            SecurityEventsActions.requestTagsSuccess({
              tags,
            }),
          ),
          catchError((error) =>
            of(SecurityEventsActions.requestTagsError({ error })),
          ),
        ),
      ),
    ),
  );

  readonly requestEditTags$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SecurityEventsActions.requestEditTags),
      withLatestFrom(this.store.select(selectSecurityEventsId)),
      switchMap(([{ tagIds }, securityEventId]) =>
        this.securityEventsApiService.editTags(securityEventId, tagIds).pipe(
          map(() => SecurityEventsActions.requestEditTagsSuccess()),
          catchError((error) =>
            of(SecurityEventsActions.requestEditTagsError({ error })),
          ),
        ),
      ),
    ),
  );

  readonly requestEditTagsSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SecurityEventsActions.requestEditTagsSuccess),
        tap(() => this.store.dispatch(SecurityEventsActions.requestTags())),
      ),
    { dispatch: false },
  );

  readonly requestDeleteSystemTag$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SecurityEventsActions.requestDeleteSystemTag),
      withLatestFrom(this.store.select(selectSecurityEventsId)),
      switchMap(([{ rmmInitEventTagId }, securityEventId]) =>
        this.securityEventsApiService
          .deleteSystemTag(securityEventId, rmmInitEventTagId)
          .pipe(
            map(() => SecurityEventsActions.requestDeleteSystemTagSuccess()),
            catchError((error) =>
              of(SecurityEventsActions.requestDeleteSystemTagError({ error })),
            ),
          ),
      ),
    ),
  );

  readonly requestDeleteSystemTagSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SecurityEventsActions.requestDeleteSystemTagSuccess),
        tap(() => this.store.dispatch(SecurityEventsActions.requestTags())),
      ),
    { dispatch: false },
  );

  readonly attemptGoToReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SecurityEventsActions.attemptGoToReport),
      withLatestFrom(this.store.select(selectSecurityEventsHasChanges)),
      switchMap(([_, hasChanges]) => {
        if (!hasChanges) {
          return of(SecurityEventsActions.goToReport());
        }

        const ref = openConfirmDialog(this.dialog);

        return ref.closed.pipe(
          switchMap((res) => {
            if (!res) {
              return EMPTY;
            }

            return of(SecurityEventsActions.goToReport());
          }),
        );
      }),
    ),
  );

  readonly goToReport$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SecurityEventsActions.goToReport),
        withLatestFrom(this.store.select(selectSecurityEventsId)),
        tap(([_, id]) =>
          this.router.navigate([`${EInitEventCardUrl.SB}/${id}/report`]),
        ),
      ),
    { dispatch: false },
  );

  readonly attemptGoToHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SecurityEventsActions.attemptGoToHistory),
      withLatestFrom(this.store.select(selectSecurityEventsHasChanges)),
      switchMap(([_, hasChanges]) => {
        if (!hasChanges) {
          return of(SecurityEventsActions.goToHistory());
        }

        return openConfirmDialog(this.dialog).closed.pipe(
          switchMap((res) => {
            if (!res) {
              return EMPTY;
            }

            return of(SecurityEventsActions.goToHistory());
          }),
        );
      }),
    ),
  );

  readonly goToHistory$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SecurityEventsActions.goToHistory),
        withLatestFrom(this.store.select(selectSecurityEventsId)),
        tap(([_, id]) =>
          this.router.navigate([`${EInitEventCardUrl.SB}/${id}/history`]),
        ),
      ),
    { dispatch: false },
  );

  readonly attemptShowOnGisMap$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SecurityEventsActions.attemptShowOnGisMap),
      withLatestFrom(this.store.select(selectSecurityEventsHasChanges)),
      switchMap(([_, hasChanges]) => {
        if (!hasChanges) {
          return of(SecurityEventsActions.showOnGisMap());
        }

        const ref = openConfirmDialog(this.dialog);

        return ref.closed.pipe(
          switchMap((res) => {
            if (!res) {
              return EMPTY;
            }

            return of(SecurityEventsActions.showOnGisMap());
          }),
        );
      }),
    ),
  );

  readonly showOnGisMap$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SecurityEventsActions.showOnGisMap),
        withLatestFrom(this.store.select(selectSecurityEventsItem)),
        tap(([_, { stationId }]) => {
          if (!stationId) {
            return;
          }

          window.open(
            `${document.location.origin}/map/city?selectStationId=${stationId}`,
            '_blank',
          );
        }),
      ),
    { dispatch: false },
  );

  readonly attemptShowOnMetroMap$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SecurityEventsActions.attemptShowOnMetroMap),
      withLatestFrom(this.store.select(selectSecurityEventsHasChanges)),
      switchMap(([_, hasChanges]) => {
        if (!hasChanges) {
          return of(SecurityEventsActions.showOnMetroMap());
        }

        const ref = openConfirmDialog(this.dialog);

        return ref.closed.pipe(
          switchMap((res) => {
            if (!res) {
              return EMPTY;
            }

            return of(SecurityEventsActions.showOnMetroMap());
          }),
        );
      }),
    ),
  );

  readonly showOnMetroMap$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SecurityEventsActions.showOnMetroMap),
        withLatestFrom(this.store.select(selectSecurityEventsItem)),
        tap(([_, { stationId }]) => {
          if (!stationId) {
            return;
          }

          window.open(
            `${document.location.origin}/map/metro?selectStationId=${stationId}`,
            '_blank',
          );
        }),
      ),
    { dispatch: false },
  );

  readonly showSucces$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          SecurityEventsActions.requestCreateItemSuccess,
          SecurityEventsActions.requestUpdateItemSuccess,
          SecurityEventsActions.requestSetToWorkingSuccess,
          SecurityEventsActions.requestSetToCompletedSuccess,
          SecurityEventsActions.requestSetToCloseSuccess,
          SecurityEventsActions.requestSetToCancelSuccess,
          SecurityEventsActions.requestSetToArchiveSuccess,
          SecurityEventsActions.requestAddParentSecurityEventSuccess,
          SecurityEventsActions.requestAddChildSecurityEventSuccess,
          SecurityEventsActions.requestAddChildDisruptionSuccess,
          SecurityEventsActions.requestDeleteChildEventSuccess,
          SecurityEventsActions.requestEditTagsSuccess,
          SecurityEventsActions.requestDeleteSystemTagSuccess,
        ),
        tap((action) => {
          let successTitle: string = null;

          switch (action.type) {
            case '[SECURITY_EVENTS] requestCreateItemSuccess':
              successTitle = this.translateService.instant(
                'EVENT.SECURITY.ADD_CARD.SUCCESS',
              );
              break;
            case '[SECURITY_EVENTS] requestUpdateItemSuccess':
              successTitle = this.translateService.instant(
                'EVENT.SECURITY.CARD.FUNCTIONS.UPDATE.SUCCESS',
              );
              break;
            case '[SECURITY_EVENTS] requestAddParentSecurityEventSuccess':
              successTitle = this.translateService.instant(
                'EVENT.SECURITY.CARD.FUNCTIONS.ADD_PARENT_SECURITY_EVENT.SUCCESS',
              );
              break;
            case '[SECURITY_EVENTS] requestAddChildSecurityEventSuccess':
              successTitle = this.translateService.instant(
                'EVENT.SECURITY.CARD.FUNCTIONS.ADD_CHILD_SECURITY_EVENT.SUCCESS',
              );
              break;
            case '[SECURITY_EVENTS] requestAddChildDisruptionSuccess':
              successTitle = this.translateService.instant(
                'EVENT.SECURITY.CARD.FUNCTIONS.ADD_CHILD_DISRUPTION.SUCCESS',
              );
              break;
            case '[SECURITY_EVENTS] requestDeleteChildEventSuccess':
              successTitle = this.translateService.instant(
                'EVENT.SECURITY.CARD.FUNCTIONS.DELETE_CHILD_EVENT.SUCCESS',
              );
              break;
            case '[SECURITY_EVENTS] requestSetToWorkingSuccess':
              successTitle = this.translateService.instant(
                'EVENT.SECURITY.CARD.FUNCTIONS.SET_TO_WORKING.SUCCESS',
              );
              break;
            case '[SECURITY_EVENTS] requestSetToCompletedSuccess':
              successTitle = this.translateService.instant(
                'EVENT.SECURITY.CARD.FUNCTIONS.SET_TO_COMPLETED.SUCCESS',
              );
              break;
            case '[SECURITY_EVENTS] requestSetToCloseSuccess':
              successTitle = this.translateService.instant(
                'EVENT.SECURITY.CARD.FUNCTIONS.SET_TO_CLOSE.SUCCESS',
              );
              break;
            case '[SECURITY_EVENTS] requestSetToCancelSuccess':
              successTitle = this.translateService.instant(
                'EVENT.SECURITY.CARD.FUNCTIONS.SET_TO_CANCEL.SUCCESS',
              );
              break;
            case '[SECURITY_EVENTS] requestSetToArchiveSuccess':
              successTitle = this.translateService.instant(
                'EVENT.SECURITY.CARD.FUNCTIONS.SET_TO_ARCHIVE.SUCCESS',
              );
              break;
            case '[SECURITY_EVENTS] requestEditTagsSuccess':
              successTitle = this.translateService.instant(
                'EVENT.SECURITY.CARD.FUNCTIONS.EDIT_TAGS.SUCCESS',
              );
              break;
            case '[SECURITY_EVENTS] requestDeleteSystemTagSuccess':
              successTitle = this.translateService.instant(
                'EVENT.SECURITY.CARD.FUNCTIONS.EDIT_TAGS.SUCCESS',
              );
              break;
            default:
              successTitle = this.translateService.instant(
                'GENERAL.SUCCESS_SAVE',
              );
          }

          this.toastService.add({ level: 'success', summary: successTitle });
        }),
      ),
    { dispatch: false },
  );

  readonly showErrors$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          SecurityEventsActions.requestCreateItemError,
          SecurityEventsActions.requestUpdateItemError,
          SecurityEventsActions.requestItemInfoError,
          SecurityEventsActions.requestSetToWorkingError,
          SecurityEventsActions.requestSetToCompletedError,
          SecurityEventsActions.requestSetToCloseError,
          SecurityEventsActions.requestSetToCancelError,
          SecurityEventsActions.requestSetToArchiveError,
          SecurityEventsActions.requestAddParentSecurityEventError,
          SecurityEventsActions.requestAddChildSecurityEventError,
          SecurityEventsActions.requestAddChildDisruptionError,
          SecurityEventsActions.requestEditTagsError,
          SecurityEventsActions.requestDeleteSystemTagError,
        ),
        tap(({ type, error }) => {
          let errorTitle: string = null;

          switch (type) {
            case '[SECURITY_EVENTS] requestCreateItemError':
              errorTitle = this.translateService.instant(
                'EVENT.SECURITY.ADD_CARD.ERROR',
              );
              break;
            case '[SECURITY_EVENTS] requestUpdateItemError':
              errorTitle = this.translateService.instant(
                'EVENT.SECURITY.CARD.FUNCTIONS.UPDATE.ERROR',
              );
              break;
            case '[SECURITY_EVENTS] requestAddParentSecurityEventError':
              errorTitle = this.translateService.instant(
                'EVENT.SECURITY.CARD.FUNCTIONS.ADD_PARENT_SECURITY_EVENT.ERROR',
              );
              break;
            case '[SECURITY_EVENTS] requestAddChildSecurityEventError':
              errorTitle = this.translateService.instant(
                'EVENT.SECURITY.CARD.FUNCTIONS.ADD_CHILD_SECURITY_EVENT.ERROR',
              );
              break;
            case '[SECURITY_EVENTS] requestAddChildDisruptionError':
              errorTitle = this.translateService.instant(
                'EVENT.SECURITY.CARD.FUNCTIONS.ADD_CHILD_DISRUPTION.ERROR',
              );
              break;
            case '[SECURITY_EVENTS] requestSetToWorkingError':
              errorTitle = this.translateService.instant(
                'EVENT.SECURITY.CARD.FUNCTIONS.SET_TO_WORKING.ERROR',
              );
              break;
            case '[SECURITY_EVENTS] requestSetToCompletedError':
              errorTitle = this.translateService.instant(
                'EVENT.SECURITY.CARD.FUNCTIONS.SET_TO_COMPLETED.ERROR',
              );
              break;
            case '[SECURITY_EVENTS] requestSetToCloseError':
              errorTitle = this.translateService.instant(
                'EVENT.SECURITY.CARD.FUNCTIONS.SET_TO_CLOSE.ERROR',
              );
              break;
            case '[SECURITY_EVENTS] requestSetToCancelError':
              errorTitle = this.translateService.instant(
                'EVENT.SECURITY.CARD.FUNCTIONS.SET_TO_CANCEL.ERROR',
              );
              break;
            case '[SECURITY_EVENTS] requestSetToArchiveError':
              errorTitle = this.translateService.instant(
                'EVENT.SECURITY.CARD.FUNCTIONS.SET_TO_ARCHIVE.ERROR',
              );
              break;
            case '[SECURITY_EVENTS] requestEditTagsError':
              errorTitle = this.translateService.instant(
                'EVENT.SECURITY.CARD.FUNCTIONS.EDIT_TAGS.ERROR',
              );
              break;
            case '[SECURITY_EVENTS] requestDeleteSystemTagError':
              errorTitle = this.translateService.instant(
                'EVENT.SECURITY.CARD.FUNCTIONS.EDIT_TAGS.DELETE_SYSTEM_ERROR',
              );
              break;
            default:
              errorTitle = this.translateService.instant(
                'GENERAL.ERROR_REQUEST',
              );
              break;
          }

          this.toastService.add({
            level: 'error',
            summary: errorTitle,
            detail: parseError(error),
          });
        }),
      ),
    { dispatch: false },
  );

  readonly showErrorForDeleteChildDisruption$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SecurityEventsActions.requestDeleteChildEventError),
        tap(({ error, eventLinkId }) => {
          let errorTitle: string = null;
          errorTitle =
            this.translateService.instant(
              'EVENT.SECURITY.CARD.FUNCTIONS.DELETE_CHILD_EVENT.ERROR',
            ) + eventLinkId;

          this.toastService.add({
            level: 'error',
            summary: errorTitle,
            detail: parseError(error),
          });
        }),
      ),
    { dispatch: false },
  );

  private getStateList(
    counts: IInitEventSecurityGridCount,
  ): ISelectItem<{ count: number }, string>[] {
    const states = this.translateService.instant('EVENT.SECURITY.STATE_LIST');
    return [
      {
        key: null,
        value: states['ALL'],
        ext: { count: counts?.totalCount ?? 0 },
      },
      {
        key: EInitEventStateCode.NEW,
        value: states['NEW'],
        ext: { count: counts?.newCount ?? 0 },
      },
      {
        key: EInitEventStateCode.WORKING,
        value: states['WORKING'],
        ext: { count: counts?.workingCount ?? 0 },
      },
    ];
  }
}
